import { FunctionComponent, useState } from "react";

import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { useGetScoreQuery } from "@/pages/Private/redux/score/score.api";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { CategoryReasoningPopover } from "@/components/CategoryReasoningPopover/CategoryReasoningPopover";
import { Dropdown } from "@/components/Dropdown/Dropdown";
import { TextArea } from "@/components/TextArea/TextArea";

import { useUpdateLeadScoringResultMutation } from "@/pages/Private/redux/list/list.api";

import { ListPerson } from "../../schema/list";

export const ModalChangeCompanyCategory: FunctionComponent<{ listPerson: ListPerson }> = ({
	listPerson,
}) => {
	const { data: score } = useGetScoreQuery();

	const { t } = useTranslation();
	const [showModal, setShowModal] = useState(false);
	const [reason, setReason] = useState("");
	const [updateLeadScoringResult, { isLoading }] = useUpdateLeadScoringResultMutation();

	const getInitialCategory = () => {
		const scoreCategory =
			listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.scoreCompanyCategory;

		if (scoreCategory) {
			return {
				id: scoreCategory.id || 0,
				title: scoreCategory.category || "",
			};
		}

		return null;
	};

	const [selectedCategory, setSelectedCategory] = useState<{
		id: number;
		title: string;
	} | null>(getInitialCategory);

	const [prevListPerson, setPrevListPerson] = useState(listPerson);

	if (prevListPerson !== listPerson) {
		setPrevListPerson(listPerson);
		setSelectedCategory(getInitialCategory());
	}

	const handleClose = () => {
		setShowModal(false);
		setReason("");
	};

	const handleSave = async () => {
		await updateLeadScoringResult({
			id: listPerson.id,
			scoreCompanyCategoryId: selectedCategory?.id || 0,
			companyResult: selectedCategory?.title || "",
			reason,
		}).unwrap();

		setShowModal(false);
		setReason("");
	};

	return (
		<div
			className="flex items-center cursor-pointer"
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();

				setShowModal(true);
			}}
		>
			<Tooltip
				content={
					<CategoryReasoningPopover
						categoryName={
							listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.result ||
							listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.companyCategory
						}
						kind="company"
						prioScore={listPerson?.prospect?.scorePersonaCategoryResult?.companyScore}
						reasoning={listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.reason}
					/>
				}
				disabled={!listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.reason}
				panel
			>
				<div className="flex flex-col">
					<span className="text-sm">
						{listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.result ||
							listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.companyCategory ||
							"Not categorized"}
					</span>

					{listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.result &&
						listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.companyCategory && (
							<span className="text-xxs text-text-disabled">
								{listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.companyCategory}
							</span>
						)}
				</div>
			</Tooltip>
			<Modal
				handleClose={(e) => {
					e.preventDefault();
					e.stopPropagation();

					handleClose();
				}}
				handleSave={(e) => {
					e.preventDefault();
					e.stopPropagation();

					handleSave();
				}}
				isLoading={isLoading}
				isOpened={showModal}
				disableSave={isLoading}
				title={t("scoring.changeCompanyCategory")}
			>
				<div>
					<div className="text-sm font-bold">
						{t("scoring.changeCompanyCategoryFor", {
							name: listPerson?.prospect?.organization?.name,
						})}
					</div>
					<Dropdown
						data={[
							...(score?.prospectCompanyCategories?.map((category) => ({
								id: category.id || 0,
								title: `${category.category} (${category.type?.capitalizeString()})`,
							})) || []),
							...(score?.partnerCompanyCategories?.map((category) => ({
								id: category.id || 0,
								title: `${category.category} (${category.type?.capitalizeString()})`,
							})) || []),
							{
								id: 0,
								title: "Disqualified",
							},
							{
								id: 0,
								title: "Competitor",
							},
						]}
						defaultValue={
							selectedCategory
								? {
										id: selectedCategory.id,
										title: selectedCategory.title,
									}
								: undefined
						}
						handleSelect={(selected) => {
							setSelectedCategory({
								id: selected.id as number,
								title: selected.title,
							});
						}}
						label={t("scoring.companyCategory")}
					/>
					<div className="mt-4">
						<TextArea
							handleChange={(e) => setReason(e.target.value)}
							label={t("scoring.reasonForChange")}
							name="reason"
							placeholder={t("scoring.reasonPlaceholder")}
							showError={false}
							value={reason}
						/>
					</div>
				</div>
			</Modal>
		</div>
	);
};
